// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import cssContent from "constants.module.scss";
import { IConnectorData } from "states/ocppstates";
import { isSet } from "shared/utils/utils";

// derived from OCPP standard
export enum ConnectorStatus {
  AVAILABLE = "Available",
  PREPARING = "Preparing",
  CHARGING = "Charging",
  SUSPENDED_EV = "SuspendedEV",
  SUSPENDED_EVSE = "SuspendedEVSE",
  FINISHING = "Finishing",
  RESERVED = "Reserved",
  UNAVAILABLE = "Unavailable",
  FAULTED = "Faulted",
}

export enum PlugStatus {
  DISCONNECTED = "Disconnected",
  CONNECTED = "Connected",
  LOCKED = "Locked",
}

// derived from OCPP standard: StopTransaction.req Reason Enumeration
export enum StopReason {
  DE_AUTHORIZED = "DeAuthorized", // The transaction was stopped because of the authorization status in a StartTransaction.conf
  EMERGENCY_STOP = "EmergencyStop", // Emergency stop button was used.
  EV_DISCONNECT = "EVDisconnected", // disconnecting of cable, vehicle moved away from inductive charge unit.
  HARD_RESET = "HardReset", // A hard reset command was received.
  LOCAL = "Local", // Stopped locally on request of the user at the Charge Point. This is a regular termination of a transaction. Examples: presenting an RFID tag, pressing a button to stop.
  OTHER = "Other", // Any other reason.
  POWER_LOSS = "PowerLoss", // Complete loss of power.
  REBOOT = "Reboot", // A locally initiated reset/reboot occurred. (for instance watchdog kicked in)
  REMOTE = "Remote", // Stopped remotely on request of the user. This is a regular termination of a transaction. Examples: termination using a smartphone app, exceeding a (non local) prepaid credit.
  SOFT_RESET = "SoftReset", // A soft reset command was received.
  UNLOCK_COMMAND = "UnlockCommand", // Central System sent an Unlock Connector command.
  // custom extensions
  UNAUTHENTICATED = "Unauthenticated", // backend refused authorization
  NOT_ENDED = "NotEnded", // session was not ended by backend but manually during DB clean-up
}

// timeout after charger is considered offline
// keep this in sync with max websocket connection time, which is 2 hours
const OFFLINE_TIMEOUT = 2 * 3600; // s

export function getColor(status: ConnectorStatus | undefined, isOnline: boolean | undefined) {
  const color =
    !isSet(status) || !isOnline ? cssContent.CHARGER_COLOR_UNDEFINED
    : status === ConnectorStatus.AVAILABLE ? cssContent.CHARGER_COLOR_AVAILABLE
    : status === ConnectorStatus.CHARGING ? cssContent.CHARGER_COLOR_CHARGING
    : (
      [
        ConnectorStatus.SUSPENDED_EV,
        ConnectorStatus.SUSPENDED_EVSE,
        ConnectorStatus.PREPARING,
        ConnectorStatus.FINISHING,
      ].includes(status)
    ) ?
      cssContent.CHARGER_COLOR_SUSPENDED
    : [ConnectorStatus.UNAVAILABLE, ConnectorStatus.FAULTED].includes(status) ?
      cssContent.CHARGER_COLOR_FAULTED
    : "violet"; // should not happen
  return color;
}

class Connector {
  public lastOcppMessage;
  public connectorNumber;
  private connectorData: (IConnectorData | undefined)[] | undefined;

  constructor(
    connectorData: (IConnectorData | undefined)[] | undefined,
    connectorNumber: number,
    lastOcppMessage: number | undefined,
  ) {
    this.connectorData = connectorData;
    this.connectorNumber = connectorNumber;
    this.lastOcppMessage = lastOcppMessage;
  }

  public get data() {
    if (isSet(this.connectorData?.[this.connectorNumber])) {
      return this.connectorData?.[this.connectorNumber];
    }
    // provide station data as fallback?
    // yes, to show data even on wrong connector numbers
    // no, to avoid masking offline connector
    // return this.connectorData?.[0];
    return undefined;
  }
  public get status() {
    return this.data?.status;
  }
  public get statusTimestamp() {
    return this.data?.statusTimestamp;
  }

  public get stationStatus() {
    return this.connectorData?.[0]?.status;
  }
  public get stationStatusTimestamp() {
    return this.connectorData?.[0]?.statusTimestamp;
  }

  public isOnline() {
    if (!isSet(this.lastOcppMessage)) {
      return undefined;
    }
    return Date.now() / 1000 - this.lastOcppMessage <= OFFLINE_TIMEOUT;
  }

  public getStatusColor() {
    return getColor(this.status, this.isOnline());
  }
}

export default Connector;
